var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useGiveawayActive } from 'root/src/client/v2/giveaways/features/GiveawayHistory/hooks/useGiveawayHistory';
import { useCreateScheduleMutation, useEditScheduleMutation, useScheduleQuery, } from 'root/src/client/v2/giveaways/hooks/api/giveawaySchedulerApi';
import { GiveawaySchedulerConfigStatus } from '@daredrop-com/rewards-api-contract';
import { Platform } from 'root/src/shared/@types/platforms';
import { ScopeSet } from 'root/src/shared/@types/auth';
import { Provider } from 'root/src/shared/@types/providers';
import { UpgradePermissionsModal } from 'root/src/client/v2/auth/components/UpgradePermissionsModal';
import toast from 'react-hot-toast';
import { colorsNew } from 'root/src/client/web/commonStyles';
import analyticsSendItem from 'root/src/client/logic/analytics/util/analyticsSendItem';
import { giveawayAnalyticsEvents } from 'root/src/shared/giveaways/giveaways.constants';
import { queryClient } from 'root/src/client/v2/common/clients/queryClient';
import { giveawaysQueryKeys } from 'root/src/shared/giveaways/giveaways.keys';
import { useUserId } from 'root/src/client/v2/auth/hooks/useUserId';
import { useCreateGiveawayMutation, } from 'root/src/client/v2/giveaways/hooks/api/giveawaysApi';
import { useModal } from 'root/src/client/v2/common/contexts/modal.context';
import { trpc } from 'root/src/client/v2/trpc/trpc';
export const useCreateGiveawayModal = (props) => {
    var _a, _b;
    const { userId } = useUserId();
    const giveawayActive = useGiveawayActive();
    const { giveaway } = giveawayActive;
    const modal = useModal();
    const utils = trpc.useUtils();
    const hideModal = (_a = props.hideModal) !== null && _a !== void 0 ? _a : modal.hide;
    const requestChatPermission = (_b = props.requestChatPermission) !== null && _b !== void 0 ? _b : (() => new Promise((resolve) => {
        modal.show(<GiveawayPermissionModal onContinue={() => resolve(true)} onSuccess={() => resolve(true)} onError={() => resolve(false)}/>, { onClose: () => resolve(false) });
    }));
    const schedulerQuery = useScheduleQuery();
    const scheduler = schedulerQuery.data;
    const giveawayConfigQuery = trpc.giveaways.getGiveawayConfig.useQuery({
        configId: scheduler === null || scheduler === void 0 ? void 0 : scheduler.configId,
    }, {
        enabled: Boolean(scheduler === null || scheduler === void 0 ? void 0 : scheduler.configId),
    });
    const editScheduleMutation = useEditScheduleMutation();
    const createGiveawayMutation = useCreateGiveawayMutation();
    const createScheduleMutation = useCreateScheduleMutation();
    const { createGiveawayConfig, upsertGiveawayConfig } = useGiveawayConfigMutations();
    const giveawayConfig = giveawayConfigQuery.data;
    const onCreateGiveaway = (data) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const hasValidToken = yield utils.auth.hasValidTokenScope.fetch({
                platform: Platform.Twitch,
                scopeSet: ScopeSet.creatorPlus,
            });
            if (!hasValidToken) {
                const shouldContinue = yield requestChatPermission();
                if (!shouldContinue) {
                    return;
                }
            }
            const configId = yield createGiveawayConfig(data);
            yield createGiveawayMutation.mutateAsync({
                repeatTimes: data.repeatTimes,
                durationTime: data.durationTime,
                configId,
            });
            analyticsSendItem(giveawayAnalyticsEvents.CREATE_GIVEAWAY, userId);
            toast.success('Successfully created giveaway', {
                style: {
                    backgroundColor: colorsNew.primary,
                    color: colorsNew.white,
                },
            });
            if (data.repeatTimes) {
                yield createScheduleMutation.mutateAsync(data);
                analyticsSendItem(giveawayAnalyticsEvents.CREATE_GIVEAWAY_SCHEDULE, userId);
            }
            void queryClient.invalidateQueries({ queryKey: giveawaysQueryKeys.giveawaysQueryKeySelector() });
            hideModal();
        }
        catch (err) {
            console.error(err);
            toast.error((err === null || err === void 0 ? void 0 : err.message) || 'Failed to create giveaway.\nPlease contact with support', { duration: 5000 });
        }
    });
    const onCreateSchedule = (data) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const configId = yield createGiveawayConfig(data);
            yield createScheduleMutation.mutateAsync({
                repeatTimes: data.repeatTimes,
                durationTime: data.durationTime,
                configId,
            });
            analyticsSendItem(giveawayAnalyticsEvents.CREATE_GIVEAWAY_SCHEDULE, userId);
            toast.success('Successfully created giveaway schedule', {
                style: {
                    backgroundColor: colorsNew.primary,
                    color: colorsNew.white,
                },
            });
            void queryClient.invalidateQueries({ queryKey: giveawaysQueryKeys.giveawaysQueryKeySelector() });
            hideModal();
        }
        catch (err) {
            console.error(err);
            const message = (err === null || err === void 0 ? void 0 : err.message) || 'Failed to create schedule.\nPlease contact with support';
            toast.error(message, { duration: 5000 });
        }
    });
    const onEditSchedule = (data) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const configId = yield upsertGiveawayConfig({
                configId: scheduler === null || scheduler === void 0 ? void 0 : scheduler.configId,
                isTwitchSubscribersOnly: data.isTwitchSubscribersOnly,
                blockBannedUsers: data.blockBannedUsers,
                maxWinners: data.maxWinners,
                maxDailyUserWins: data.maxDailyUserWins,
                voucherCountPerWinner: data.voucherCountPerWinner,
                minParticipantsToStart: data.minParticipantsToStart,
                durationTime: data.durationTime,
                maxWaitTime: data.maxWaitTime,
                isMinParticipantsEnabled: data.isMinParticipantsEnabled,
            });
            yield editScheduleMutation.mutateAsync({
                id: data.id,
                durationTime: data.durationTime,
                repeatTimes: data.repeatTimes,
                configId,
            });
            analyticsSendItem(giveawayAnalyticsEvents.EDIT_GIVEAWAY_SCHEDULE, userId);
            toast.success('Successfully edited giveaway schedule', {
                style: {
                    backgroundColor: colorsNew.primary,
                    color: colorsNew.white,
                },
            });
            void queryClient.invalidateQueries({ queryKey: giveawaysQueryKeys.giveawaysQueryKeySelector() });
            hideModal();
        }
        catch (err) {
            console.error(err);
            const message = (err === null || err === void 0 ? void 0 : err.message) || 'Failed to update schedule.\nPlease contact with support';
            toast.error(message, { duration: 5000 });
        }
    });
    const submitGiveaway = (data) => __awaiter(void 0, void 0, void 0, function* () {
        if (scheduler) {
            return onEditSchedule(Object.assign({ id: scheduler.id }, data));
        }
        if (giveaway) {
            return onCreateSchedule(data);
        }
        return onCreateGiveaway(data);
    });
    const onSubmit = (data) => __awaiter(void 0, void 0, void 0, function* () {
        yield submitGiveaway(data);
        if (props.onSuccess) {
            yield props.onSuccess();
        }
    });
    const onCancelScheduler = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!scheduler) {
            return;
        }
        yield editScheduleMutation.mutateAsync({
            id: scheduler.id,
            status: GiveawaySchedulerConfigStatus.Inactive,
        });
        analyticsSendItem(giveawayAnalyticsEvents.CANCEL_GIVEAWAY_SCHEDULE, userId);
        toast.success('Giveaway schedule has been canceled', {
            style: {
                backgroundColor: colorsNew.primary,
                color: colorsNew.white,
            },
        });
        yield schedulerQuery.refetch();
        hideModal();
    });
    return {
        onSubmit,
        onCancelScheduler,
        giveaway,
        scheduler,
        giveawayConfig,
        isLoading: giveawayActive.isLoading || schedulerQuery.isLoading || schedulerQuery.isFetching
            || giveawayConfigQuery.isLoading || giveawayConfigQuery.isFetching,
    };
};
const GiveawayPermissionModal = ({ onContinue, onSuccess, onError }) => {
    return (<UpgradePermissionsModal autoLabel="I want to automatically send Giveaway announcements to my chat (Recommended)" manualLabel="I will announce the Giveaway on my own (Not recommended)" scopeSet={ScopeSet.creatorPlus} provider={Provider.Twitch} onContinue={onContinue} onSuccess={onSuccess} onError={onError}/>);
};
const useGiveawayConfigMutations = () => {
    const createGiveawayConfigMutation = trpc.giveaways.createGiveawayConfig.useMutation();
    const updateGiveawayConfigMutation = trpc.giveaways.updateGiveawayConfig.useMutation();
    const createGiveawayConfig = (data) => __awaiter(void 0, void 0, void 0, function* () {
        const shouldCreateConfig = data.isTwitchSubscribersOnly || data.blockBannedUsers
            || data.maxDailyUserWins || data.maxWinners > 1 || data.voucherCountPerWinner || data.isMinParticipantsEnabled;
        if (shouldCreateConfig) {
            const config = yield createGiveawayConfigMutation.mutateAsync({
                isTwitchSubscribersOnly: data.isTwitchSubscribersOnly,
                blockBannedUsers: data.blockBannedUsers,
                maxWinners: data.maxWinners,
                maxDailyUserWins: data.maxDailyUserWins,
                voucherCountPerWinner: data.voucherCountPerWinner,
                // Only include minParticipantsToStart, durationTime and maxWaitTime when minParticipants is enabled
                minParticipantsToStart: data.isMinParticipantsEnabled ? data.minParticipantsToStart : undefined,
                durationTime: data.isMinParticipantsEnabled ? data.durationTime : undefined,
                maxWaitTime: data.isMinParticipantsEnabled ? data.maxWaitTime : undefined,
            });
            return config.id;
        }
        return undefined;
    });
    const upsertGiveawayConfig = (data) => __awaiter(void 0, void 0, void 0, function* () {
        if (!data.configId) {
            return createGiveawayConfig(data);
        }
        const config = yield updateGiveawayConfigMutation.mutateAsync({
            configId: data.configId,
            isTwitchSubscribersOnly: data.isTwitchSubscribersOnly,
            blockBannedUsers: data.blockBannedUsers,
            maxWinners: data.maxWinners,
            maxDailyUserWins: data.maxDailyUserWins,
            voucherCountPerWinner: data.voucherCountPerWinner,
            // Only include minParticipantsToStart, durationTime and maxWaitTime when minParticipants is enabled
            minParticipantsToStart: data.isMinParticipantsEnabled ? data.minParticipantsToStart : null,
            durationTime: data.isMinParticipantsEnabled ? data.durationTime : undefined,
            maxWaitTime: data.isMinParticipantsEnabled ? data.maxWaitTime : undefined,
        });
        return config.id;
    });
    return { createGiveawayConfig, upsertGiveawayConfig };
};
